import { Branding, GeometryEntry, GeometryUpdate } from "@org-avp/avp-avengers-geometry-service-api";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useMemo } from "react";
import { geometryApi } from "../apis/geometry-service-api";
import { createCachedLookup } from "../utils/lookup.utils";

const pollingIntervalInMS = 5_000;

export interface GeometryHelper {
  geometryData: {
    data?: GeometryUpdate;
    isLoading: boolean;
    error?: unknown;
  };

  createGeometryEntryLookup(): Map<string, GeometryEntry>;
}

export const useGeometryHelper = (branding?: Branding, geometryIds?: string[]): GeometryHelper => {
  const sortedDistinctGeometryIds = useMemo(
    () => geometryIds ? Array.from(new Set(geometryIds)).sort() : undefined,
    [geometryIds],
  );

  const geometriesByBrand = geometryApi.useGetAllGeometriesQuery(branding && !geometryIds ? [branding] : skipToken);
  geometryApi.useGetUpdatedGeometriesQuery(
    branding && !geometryIds && geometriesByBrand.data ? [branding, geometriesByBrand.data.lastModified] : skipToken,
    { pollingInterval: pollingIntervalInMS },
  );

  const geometriesBulk = geometryApi.useGetGeometriesBulkQuery(branding && sortedDistinctGeometryIds ? [branding, sortedDistinctGeometryIds] : skipToken);
  geometryApi.useGetUpdatedGeometriesBulkQuery(
    branding && sortedDistinctGeometryIds && geometriesBulk.data ? [branding, sortedDistinctGeometryIds, geometriesBulk.data.lastModified] : skipToken,
    { pollingInterval: pollingIntervalInMS },
  );

  const geometryData = useMemo(() => geometryIds ? geometriesBulk : geometriesByBrand, [geometryIds, geometriesBulk, geometriesByBrand]);
  const createGeometryEntryLookup = useCallback(
    () => cacheGeometryEntryLookup(geometryData.data),
    [geometryData.data],
  );

  return useMemo(() => ({
    geometryData,
    createGeometryEntryLookup,
  }), [geometryData, createGeometryEntryLookup]);
};

const geometryLookupCache = new WeakMap<GeometryUpdate, Map<string, GeometryEntry>>();
const cacheGeometryEntryLookup = (geometry: GeometryUpdate | undefined) => {
  return createCachedLookup(
    geometryLookupCache,
    geometry,
    (it) => it.content,
    (it) => it.geometryId,
  );
};

export interface TestProps<B> {
  data: B[];
  onApply: (data: B) => void;
}
